import Button from '@app-studio-web/ui/components/Button'
import InfoBox from '@app-studio-web/ui/components/InfoBox'
import Layout from '@app-studio-web/ui/components/Layout'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form } from 'react-router-dom'

interface SliderInputPageProps {
  heading: string
  description?: string
  showSkipButton?: boolean
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void
  onSkip?: () => void
  max?: number
  inputNameEndurance?: string
  inputNameSpeed?: string
  valueEndurance?: string
  valueSpeed?: string
  leftLabel?: string
  rightLabel?: string
}

export default function SliderInputPage({
  heading,
  description,
  showSkipButton = false,
  onSubmit,
  onSkip,
  max = 50,
  inputNameEndurance = 'endurance',
  inputNameSpeed = 'speed',
  valueEndurance: preselectedEndurance,
  valueSpeed: preselectedSpeed,
  leftLabel,
  rightLabel,
}: SliderInputPageProps) {
  const { t } = useTranslation()
  const trackRef = useRef<HTMLDivElement>(null)
  // For this custom slider:
  // Speed: 0 (center) to 50 (left end)
  // Endurance: 0 (center) to 50 (right end)
  const [speed, setSpeed] = useState(0) // left thumb, moves left from center
  const [endurance, setEndurance] = useState(0) // right thumb, moves right from center

  useEffect(() => {
    if (preselectedEndurance) {
      const num = parseInt(preselectedEndurance, 10)
      if (!isNaN(num) && num >= 0 && num <= max) setEndurance(num)
    } else {
      setEndurance(0)
    }
    if (preselectedSpeed) {
      const num = parseInt(preselectedSpeed, 10)
      if (!isNaN(num) && num >= 0 && num <= max) setSpeed(num)
    } else {
      setSpeed(0)
    }
  }, [preselectedEndurance, preselectedSpeed, max])

  // Helper: get pixel position for a value (0=center, 50=right/left end)
  const getThumbPosition = (which: 'speed' | 'endurance', value: number) => {
    const gap = 6 // 6px gap on each side (12px total between thumbs when both are at 0)
    if (which === 'speed') {
      // Speed: 0=center-gap, 50=left end
      return `calc(50% - ${gap}px - ${(value / max) * 50}%)`
    } else {
      // Endurance: 0=center+gap, 50=right end
      return `calc(50% + ${gap}px + ${(value / max) * 50}%)`
    }
  }

  // Get value from clientX for each thumb
  const getValueFromClientX = (
    which: 'speed' | 'endurance',
    clientX: number,
  ) => {
    const track = trackRef.current
    if (!track) return 0
    const rect = track.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const gap = 6 // 6px gap on each side

    if (which === 'speed') {
      // Only allow moving left from center-gap
      const minX = centerX - gap
      if (clientX > minX) return 0
      const percent = (minX - clientX) / (rect.width / 2)
      return Math.round(Math.max(0, Math.min(percent, 1)) * max)
    } else {
      // Only allow moving right from center+gap
      const minX = centerX + gap
      if (clientX < minX) return 0
      const percent = (clientX - minX) / (rect.width / 2)
      return Math.round(Math.max(0, Math.min(percent, 1)) * max)
    }
  }

  const handleThumbDown =
    (which: 'speed' | 'endurance') =>
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault()
      const moveHandler = (event: Event) => {
        let clientX = 0
        if (event instanceof TouchEvent && event.touches.length > 0) {
          clientX = event.touches[0].clientX
        } else if (event instanceof MouseEvent) {
          clientX = event.clientX
        }
        const value = getValueFromClientX(which, clientX)
        if (which === 'speed') {
          setSpeed(value)
        } else {
          setEndurance(value)
        }
      }
      const upHandler = () => {
        document.removeEventListener('mousemove', moveHandler)
        document.removeEventListener('mouseup', upHandler)
        document.removeEventListener('touchmove', moveHandler)
        document.removeEventListener('touchend', upHandler)
      }
      document.addEventListener('mousemove', moveHandler)
      document.addEventListener('mouseup', upHandler)
      document.addEventListener('touchmove', moveHandler, {
        passive: false,
      })
      document.addEventListener('touchend', upHandler)
    }

  // Fill style: left of center for speed, right of center for endurance
  const getSpeedFillStyle = (): React.CSSProperties => {
    const gap = 6 // 6px gap on each side
    return {
      left: `calc(50% - ${gap}px - ${(speed / max) * 50}%)`,
      width: `calc(${(speed / max) * 50}%)`,
    }
  }
  const getEnduranceFillStyle = (): React.CSSProperties => {
    const gap = 6 // 6px gap on each side
    return {
      left: `calc(50% + ${gap}px)`,
      width: `calc(${(endurance / max) * 50}%)`,
    }
  }

  return (
    <Layout>
      <Form className="flex h-full flex-col" method="post" onSubmit={onSubmit}>
        <input type="hidden" name={inputNameEndurance} value={endurance} />
        <input type="hidden" name={inputNameSpeed} value={speed} />
        <div className="mt-8">
          <h1 className="custom-heading text-center text-3xl whitespace-pre-line">
            {heading}
          </h1>
        </div>

        <div className="flex flex-1 flex-col items-center justify-center gap-8">
          {description && <InfoBox>{description}</InfoBox>}

          <div className="mx-auto w-full max-w-md px-4">
            <div className="mb-2 flex justify-between text-sm text-gray-600">
              <span>{leftLabel}</span>
              <span>{rightLabel}</span>
            </div>

            <div
              ref={trackRef}
              className="relative h-2 w-full cursor-pointer rounded-full bg-gray-300"
            >
              {/* Speed fill (left of center) */}
              <div
                className="absolute top-0 h-full rounded-l-full bg-black"
                style={getSpeedFillStyle()}
              />
              {/* Center fill (between thumbs) */}
              <div
                className="absolute top-0 h-full bg-black"
                style={{
                  left: `calc(50% - 6px)`,
                  width: `12px`,
                }}
              />
              {/* Endurance fill (right of center) */}
              <div
                className="absolute top-0 h-full rounded-r-full bg-black"
                style={getEnduranceFillStyle()}
              />
              {/* Speed Thumb (left, moves from center to left) */}
              <div
                className="absolute top-1/2 z-10 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black shadow-md"
                style={{ left: getThumbPosition('speed', speed) }}
                onMouseDown={handleThumbDown('speed')}
                onTouchStart={handleThumbDown('speed')}
                tabIndex={0}
                role="slider"
                aria-valuenow={speed}
                aria-valuemin={0}
                aria-valuemax={max}
                aria-label="Speed"
              />
              {/* Endurance Thumb (right, moves from center to right) */}
              <div
                className="absolute top-1/2 z-10 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black shadow-md"
                style={{ left: getThumbPosition('endurance', endurance) }}
                onMouseDown={handleThumbDown('endurance')}
                onTouchStart={handleThumbDown('endurance')}
                tabIndex={0}
                role="slider"
                aria-valuenow={endurance}
                aria-valuemin={0}
                aria-valuemax={max}
                aria-label="Endurance"
              />
            </div>
          </div>
        </div>

        <div className="mx-auto mt-auto mb-8 flex w-full max-w-[500px] flex-col gap-4">
          {showSkipButton && onSkip && (
            <button
              type="button"
              onClick={onSkip}
              className="w-full rounded-[20px] border border-[#ACACAC] bg-white px-7 py-5 text-black transition-colors hover:bg-gray-50"
            >
              {t('buttons.skip')}
            </button>
          )}
          <Button
            title={t('buttons.continue')}
            type="submit"
            dataTestId="button-continue-input"
          />
        </div>
      </Form>
    </Layout>
  )
}
